import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Code2, Megaphone, Sparkles, ArrowRight, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { luxe, PILLARS, gradientCss } from '@/lib/luxe';
import { GlassCard, LivePill } from '@/components/bits';

const ICONS = { Code2, Megaphone, Sparkles };

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div>
      <div className="mb-8 float-in">
        <div className="text-[11px] font-extrabold uppercase tracking-[0.2em] mb-2 gold-text">MyRaai Studio</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black" style={{ color: luxe.text, letterSpacing: -1 }}>
          Hey {user?.name?.split(' ')[0]}, <span className="blue-text">what will you</span> <span className="gold-text">create today?</span>
        </h1>
        <p className="mt-3 text-base max-w-2xl" style={{ color: luxe.textMid }}>
          Pick a pillar to begin — coding, marketing or creative. Premium, consistent output every time.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        {Object.values(PILLARS).map((p, i) => {
          const Icon = ICONS[p.icon];
          return (
            <GlassCard
              key={p.key}
              data-testid={`pillar-card-${p.key}`}
              className="p-7 cursor-pointer hover-lift float-in relative overflow-hidden"
              style={{ animationDelay: `${i * 90}ms`, minHeight: 285, border: `2px solid ${p.primary}`, background: p.key === "coding" ? "linear-gradient(145deg,#E7FFFC,#BDF5EF)" : p.key === "marketing" ? "linear-gradient(145deg,#FFFCE5,#FFE65A)" : "#FFFFFF", boxShadow: `0 18px 42px ${p.glow}`, color: "#4B1727" }}
              onClick={() => navigate(p.path)}
            >
              <div className="absolute -top-10 -right-10 w-36 h-36 rounded-full opacity-30 pointer-events-none" style={{ background: gradientCss(p.grad), filter: "blur(26px)" }} />
              <div className="relative">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ background: gradientCss(p.grad), boxShadow: `0 10px 26px ${p.glow}` }}>
                  <Icon size={28} color="#4B1727" strokeWidth={2.5} />
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <LivePill color={p.primary} />
                </div>
                <h3 className="font-display text-2xl font-black" style={{ color: "#4B1727" }}>{p.name}</h3>
                <p className="mt-2 text-sm" style={{ color: "#4B1727" }}>{p.tagline}</p>
                <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold luxe-btn" data-testid={`enter-${p.key}`} style={{ background: "#4B1727", color: "#FFFFFF", boxShadow: "0 8px 18px rgba(75,23,39,0.20)" }}>
                  Enter <ArrowRight size={16} />
                </div>
              </div>
            </GlassCard>
          );
        })}
      </div>

      <GlassCard className="mt-6 p-6 float-in" style={{ animationDelay: '300ms' }}>
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(37,99,235,0.1)', border: '1px solid rgba(37,99,235,0.25)' }}>
            <ShieldCheck size={20} style={{ color: luxe.blue }} />
          </div>
          <div>
            <h4 className="font-bold text-lg" style={{ color: luxe.text }}>Zero-Trust Agent Firewall active</h4>
            <p className="mt-1 text-sm" style={{ color: luxe.textMid }}>
              Every AI call is scanned for prompt-injection and scrubbed for secrets before anything reaches you. Your creations stay safe and brand-safe.
            </p>
          </div>
        </div>
      </GlassCard>
    </div>
  );
}
